import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle, Clock, TrendingUp, HelpCircle, ExternalLink, Zap, Loader2, Eye, RefreshCw, AlertCircle, Download, Users } from "lucide-react";
import { capiBackfillPurchase, capiEventsList, downloadLandingLeadsCsv, downloadAuditLeadsCsv, fetchLandingLeadsSummary } from "./api";

const OBJECTION_META = {
  price: { label: "Price objection", bg: "#FAECE7", color: "#712B13" },
  timing: { label: "Timing / not ready", bg: "#FAEEDA", color: "#633806" },
  trust: { label: "Trust / proof", bg: "#E6F1FB", color: "#0C447C" },
  diy: { label: "DIY objection", bg: "#EEEDFE", color: "#3C3489" },
  competitor: { label: "Comparing options", bg: "#F1EFE8", color: "#444441" },
};

export default function AdminIntelPanel({ leads }) {
  const [open, setOpen] = useState(false);
  const [capiBusy, setCapiBusy] = useState(false);
  const [capiResult, setCapiResult] = useState(null);
  const [forceFire, setForceFire] = useState(false);

  const [logBusy, setLogBusy] = useState(false);
  const [logEvents, setLogEvents] = useState(null);
  const [logFilter, setLogFilter] = useState("Purchase");

  const [expBusy, setExpBusy] = useState(null); // "landing" | "audit" | null
  const [expMsg, setExpMsg] = useState(null);
  const [landSum, setLandSum] = useState(null);
  const [landSumBusy, setLandSumBusy] = useState(false);

  const runExport = async (kind) => {
    setExpBusy(kind);
    setExpMsg(null);
    try {
      if (kind === "landing") await downloadLandingLeadsCsv();
      else await downloadAuditLeadsCsv();
      setExpMsg({ ok: true, text: "Downloaded ✓" });
    } catch (e) {
      setExpMsg({ ok: false, text: e?.message || "Export failed" });
    } finally {
      setExpBusy(null);
    }
  };

  const refreshLandingPreview = async () => {
    setLandSumBusy(true);
    try {
      const r = await fetchLandingLeadsSummary();
      setLandSum(r);
    } catch (e) {
      setLandSum({ error: e?.message || "Failed" });
    } finally {
      setLandSumBusy(false);
    }
  };

  const runCapiBackfill = async (dry) => {
    setCapiBusy(true);
    setCapiResult(null);
    try {
      const r = await capiBackfillPurchase({ dry_run: dry, force: forceFire });
      setCapiResult({ ok: true, dry, force: forceFire, ...r });
    } catch (e) {
      setCapiResult({ ok: false, dry, error: e?.message || "Backfill failed" });
    } finally {
      setCapiBusy(false);
    }
  };

  const fetchLog = async () => {
    setLogBusy(true);
    try {
      const r = await capiEventsList({ limit: 25, event_name: logFilter === "all" ? null : logFilter });
      setLogEvents(r);
    } catch (e) {
      setLogEvents({ error: e?.message || "Fetch failed" });
    } finally {
      setLogBusy(false);
    }
  };

  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const weekDemos = leads.filter((l) => l.demoDoneAt && new Date(l.demoDoneAt) >= weekAgo);

  // Card 1: Most common objection
  const objCounts = {};
  weekDemos.forEach((l) => {
    (l.objectionTags || []).forEach((tag) => {
      objCounts[tag] = (objCounts[tag] || 0) + 1;
    });
  });
  const topObj = Object.entries(objCounts).sort((a, b) => b[1] - a[1])[0];
  const topObjMeta = topObj ? OBJECTION_META[topObj[0]] : null;

  // Card 2: Avg call duration
  const durations = weekDemos.map((l) => l.callDurationMinutes).filter(Boolean);
  const avgDuration = durations.length > 0 ? (durations.reduce((a, b) => a + b, 0) / durations.length).toFixed(1) : null;
  const durInRange = avgDuration >= 12 && avgDuration <= 18;
  const durColor = !avgDuration ? "#888" : durInRange ? "#1D9E75" : avgDuration < 10 || avgDuration > 22 ? "#DC2626" : "#D97706";

  // Card 3: Calls flagged
  const flagged = weekDemos.filter((l) => l.durationFlag);
  const shortCount = flagged.filter((l) => l.durationFlag === "short").length;
  const longCount = flagged.filter((l) => l.durationFlag === "long").length;

  // Card 4: Unmatched (from leads without fathomSyncedAt but with demoDoneAt)
  const unmatchedCount = weekDemos.filter((l) => l.demoDoneAt && !l.fathomSyncedAt).length;

  return (
    <div className="admin-intel" data-testid="admin-intel-panel">
      <button className="admin-intel-toggle" onClick={() => setOpen(!open)} data-testid="admin-intel-toggle">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <TrendingUp className="w-4 h-4" />
        <span>Call intelligence this week</span>
        <span className="admin-intel-count">{weekDemos.length} demos</span>
      </button>

      {open && (
        <div className="admin-intel-cards" data-testid="admin-intel-cards">
          {/* Card 1 */}
          <div className="admin-intel-card" data-testid="intel-card-objection">
            <span className="admin-card-label">Most common objection</span>
            {topObj ? (
              <>
                <span className="admin-card-tag" style={{ background: topObjMeta?.bg, color: topObjMeta?.color }}>
                  {topObjMeta?.label}
                </span>
                <span className="admin-card-sub">{topObj[1]} of {weekDemos.length} demos ({Math.round((topObj[1] / weekDemos.length) * 100)}%)</span>
              </>
            ) : (
              <span className="admin-card-empty">No objection data yet</span>
            )}
          </div>

          {/* Card 2 */}
          <div className="admin-intel-card" data-testid="intel-card-duration">
            <span className="admin-card-label">Avg call duration</span>
            <span className="admin-card-value" style={{ color: durColor }}>
              {avgDuration ? `${avgDuration} min` : "No data"}
            </span>
            {avgDuration && (
              <span className="admin-card-sub" style={{ color: durColor }}>
                Target: 12–18 min {durInRange ? "✓" : ""}
              </span>
            )}
          </div>

          {/* Card 3 */}
          <div className="admin-intel-card" data-testid="intel-card-flagged">
            <span className="admin-card-label">Calls flagged</span>
            {flagged.length > 0 ? (
              <>
                <span className="admin-card-value">{flagged.length} calls need review</span>
                <span className="admin-card-sub">{shortCount} short · {longCount} long</span>
                <div className="admin-flagged-list">
                  {flagged.map((l) => (
                    <span key={l.id} className={`admin-flagged-name admin-flag-${l.durationFlag}`}>
                      {l.clientName} ({l.callDurationMinutes}m)
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <span className="admin-card-empty">No flagged calls</span>
            )}
          </div>

          {/* Card 4 */}
          <div className="admin-intel-card" data-testid="intel-card-unmatched">
            <span className="admin-card-label">Awaiting Fathom sync</span>
            {unmatchedCount > 0 ? (
              <span className="admin-card-value">{unmatchedCount} demos without call data</span>
            ) : (
              <span className="admin-card-empty" style={{ color: "#1D9E75" }}>All demos synced</span>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className="admin-capi-panel" data-testid="admin-leads-export-panel">
          <div className="admin-capi-header">
            <Users className="w-4 h-4" style={{ color: "#059669" }} />
            <span>Landing & Audit lead exports</span>
            <span className="admin-capi-help">
              Download every lead captured on bulkjobapply.com — landing form (name, WhatsApp, email, target role, source) and Audit funnel (with AI diagnosis and chosen path). Both live in Mongo and are also pushed to Brevo when configured.
            </span>
          </div>
          <div className="admin-capi-actions">
            <button
              className="admin-capi-btn admin-capi-btn-primary"
              onClick={() => runExport("landing")}
              disabled={expBusy !== null}
              data-testid="export-landing-csv"
            >
              {expBusy === "landing" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Landing leads CSV
            </button>
            <button
              className="admin-capi-btn admin-capi-btn-secondary"
              onClick={() => runExport("audit")}
              disabled={expBusy !== null}
              data-testid="export-audit-csv"
            >
              {expBusy === "audit" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Audit leads CSV
            </button>
            <button
              className="admin-capi-btn admin-capi-btn-secondary"
              onClick={refreshLandingPreview}
              disabled={landSumBusy}
              data-testid="preview-latest-landing"
              title="Peek at the 5 most recent landing form submissions"
            >
              {landSumBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
              Latest 5
            </button>
          </div>

          {expMsg && (
            <div
              className={`admin-capi-result ${expMsg.ok ? "admin-capi-result-success" : "admin-capi-result-info"}`}
              data-testid="export-result"
              style={!expMsg.ok ? { borderColor: "#fecaca", background: "#fef2f2", color: "#b91c1c" } : undefined}
            >
              {expMsg.text}
            </div>
          )}

          {landSum && !landSum.error && (
            <div className="admin-capi-result admin-capi-result-info" data-testid="landing-preview">
              <strong>{landSum.count}</strong> most-recent lead(s):
              <ul className="admin-capi-list">
                {(landSum.leads || []).map((l) => (
                  <li key={l.id}>
                    <span>{l.first_name || "—"} · {l.target_role || "—"} · {l.email || "no email"}</span>
                    <span className="admin-capi-amt">{l.whatsapp_e164 || ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {landSum?.error && (
            <div className="admin-capi-result" style={{ borderColor: "#fecaca", background: "#fef2f2", color: "#b91c1c" }} data-testid="landing-preview-error">
              {landSum.error}
            </div>
          )}
        </div>
      )}

      {open && (
        <div className="admin-capi-panel" data-testid="admin-capi-panel">
          <div className="admin-capi-header">
            <Zap className="w-4 h-4" style={{ color: "#7C3AED" }} />
            <span>Meta CAPI · backfill Purchase events</span>
            <span className="admin-capi-help">
              Fires Meta CAPI Purchase for any paid lead that hasn't been attributed yet. Skips already-fired leads automatically.
            </span>
          </div>
          <div className="admin-capi-actions">
            <button
              className="admin-capi-btn admin-capi-btn-secondary"
              onClick={() => runCapiBackfill(true)}
              disabled={capiBusy}
              data-testid="capi-backfill-preview"
            >
              {capiBusy && capiResult?.dry === true ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Preview (dry run)
            </button>
            <button
              className="admin-capi-btn admin-capi-btn-primary"
              onClick={() => runCapiBackfill(false)}
              disabled={capiBusy}
              data-testid="capi-backfill-fire"
            >
              {capiBusy && capiResult?.dry === false ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              Fire CAPI Purchases
            </button>
            <label
              className={`admin-capi-force ${forceFire ? "admin-capi-force-on" : ""}`}
              data-testid="capi-force-toggle"
              title="Re-fire even for leads that already have a Purchase event logged"
            >
              <input
                type="checkbox"
                checked={forceFire}
                onChange={(e) => setForceFire(e.target.checked)}
                data-testid="capi-force-checkbox"
              />
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Force re-fire</span>
            </label>
          </div>
          {forceFire && (
            <div className="admin-capi-force-warning" data-testid="capi-force-warning">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Force is ON — every paid lead will fire a Purchase event, even if one already exists. Use only when Meta lost an event or you're re-attributing under a new pixel.</span>
            </div>
          )}

          {capiResult && capiResult.ok && capiResult.dry && (
            <div className="admin-capi-result admin-capi-result-info" data-testid="capi-result-dry">
              <strong>{capiResult.would_fire?.length || 0}</strong> lead(s) ready to fire ·{" "}
              <strong>{capiResult.skipped?.length || 0}</strong> skipped (already fired or no phone)
              {capiResult.would_fire?.length > 0 && (
                <ul className="admin-capi-list">
                  {capiResult.would_fire.slice(0, 8).map((c) => (
                    <li key={c.id}>
                      <span>{c.name || c.id}</span>
                      <span className="admin-capi-amt">${c.amount}</span>
                    </li>
                  ))}
                  {capiResult.would_fire.length > 8 && (
                    <li className="admin-capi-more">+ {capiResult.would_fire.length - 8} more…</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {capiResult && capiResult.ok && !capiResult.dry && (
            <div className="admin-capi-result admin-capi-result-success" data-testid="capi-result-fired">
              ✓ Fired <strong>{capiResult.fired_count || 0}</strong> Purchase event(s) ·{" "}
              {capiResult.skipped?.length || 0} skipped
              {capiResult.fired?.length > 0 && (
                <ul className="admin-capi-list">
                  {capiResult.fired.slice(0, 8).map((c) => (
                    <li key={c.id}>
                      <span>{c.name || c.id}</span>
                      <span className="admin-capi-amt">${c.amount}</span>
                    </li>
                  ))}
                  {capiResult.fired.length > 8 && (
                    <li className="admin-capi-more">+ {capiResult.fired.length - 8} more…</li>
                  )}
                </ul>
              )}
            </div>
          )}

          {capiResult && !capiResult.ok && (
            <div className="admin-capi-result admin-capi-result-error" data-testid="capi-result-error">
              ✗ {capiResult.error}
            </div>
          )}

          {/* Event Log viewer */}
          <div className="admin-capi-log" data-testid="admin-capi-log">
            <div className="admin-capi-log-header">
              <Eye className="w-4 h-4" style={{ color: "#7C3AED" }} />
              <span>Recent CAPI events</span>
              <select
                className="admin-capi-log-filter"
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value)}
                data-testid="capi-log-filter"
              >
                <option value="Purchase">Purchase</option>
                <option value="Lead">Lead</option>
                <option value="InitiateCheckout">InitiateCheckout</option>
                <option value="all">All events</option>
              </select>
              <button
                className="admin-capi-btn admin-capi-btn-secondary"
                onClick={fetchLog}
                disabled={logBusy}
                data-testid="capi-log-fetch"
              >
                {logBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Fetch last 25
              </button>
            </div>
            {logEvents && logEvents.error && (
              <div className="admin-capi-result admin-capi-result-error">✗ {logEvents.error}</div>
            )}
            {logEvents && !logEvents.error && (
              <div className="admin-capi-log-body" data-testid="capi-log-body">
                <div className="admin-capi-log-stats">
                  <span><strong>{logEvents.total}</strong> total</span>
                  <span><strong>{logEvents.successful}</strong> ok</span>
                  <span><strong>{logEvents.ctwa_attributed}</strong> click-to-WA</span>
                </div>
                {logEvents.events?.length === 0 && (
                  <div className="admin-capi-log-empty">No events match this filter yet.</div>
                )}
                {logEvents.events?.length > 0 && (
                  <ul className="admin-capi-log-list">
                    {logEvents.events.slice(0, 25).map((ev, i) => (
                      <li key={`${ev.event_id || i}`} className={`admin-capi-log-row ${ev.success ? "" : "admin-capi-log-row-fail"}`}>
                        <span className="admin-capi-log-name">{ev.event_name}</span>
                        <span className="admin-capi-log-lead">{ev.lead_id?.slice(0, 8) || "—"}</span>
                        <span className="admin-capi-log-time">
                          {ev.fired_at ? new Date(ev.fired_at).toLocaleString() : "—"}
                        </span>
                        <span className={`admin-capi-log-status ${ev.success ? "ok" : "fail"}`}>
                          {ev.success ? "✓" : "✗"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
